import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  ScheduleSlot,
  ScheduleSlotsResponse,
} from '../models/schedule.model';
import { toScheduleProductType } from '../utils/mind-order-payload.utils';

import { ApiService } from './api.service';

export interface SchedulePmeCnpjInput {
  homePass: string;
  regionId: number;
  installationAddress: {
    zipcode: string;
    number: string;
  };
  products: {
    id: number;
    type: string;
    name: string;
    price: number;
  }[];
}

export interface BuildSchedulePayloadParams {
  regionId: string;
  installationLocation: { cep?: string; addressNumber?: string } | null;
  installationAddressFallback: { zipCode?: string; number?: string | number } | null;
  homePass: string | null;
  products: { id: number; type: string; name: string; price: number }[];
}

interface SchedulePmeCnpjRequest {
  regionId: number;
  homePass: string;
  installationAddress: {
    zipcode: string;
    number: string;
  };
  catalog: string;
  mediaChannel: string;
  salesChannel: string;
  products: {
    id: number;
    type: string;
    name: string;
    price: number;
  }[];
}

interface ScheduleDateApiItem {
  description: string;
  emergency: boolean;
  startDate: string;
  endDate: string;
  minutesAvailable: number;
  periodTypeId: number;
  proposal?: {
    startDate: string;
    endDate: string;
  };
}

export interface SchedulePmeCnpjResponse {
  dates: ScheduleDateApiItem[];
  installationAddress: {
    zipcode: string;
    number: string;
  };
}

const SCHEDULE_FIXED_CHANNELS = {
  catalog: 'net-empresas',
  mediaChannel: 'desktop',
  salesChannel: 'externo',
} as const;

@Injectable({
  providedIn: 'root',
})
export class ScheduleService {
  private readonly baseUrl = `${environment.externalGateServiceUrl}/mind/schedules/pme/cnpj`;
  private apiService = inject(ApiService);


  get fixedChannels(): typeof SCHEDULE_FIXED_CHANNELS {
    return SCHEDULE_FIXED_CHANNELS;
  }

  buildSchedulePayload(
    params: BuildSchedulePayloadParams
  ): SchedulePmeCnpjInput | null {
    const { regionId, installationLocation, installationAddressFallback, homePass, products } = params;
    const regionIdNum = parseInt(regionId || '0', 10);
    if (Number.isNaN(regionIdNum) || regionIdNum <= 0) return null;
    if (!products?.length) return null;

    const zipcode = (installationLocation?.cep ?? installationAddressFallback?.zipCode ?? '')
      .toString()
      .replace(/\D/g, '');
    const number =
      installationLocation?.addressNumber != null
        ? String(installationLocation.addressNumber)
        : installationAddressFallback?.number != null
          ? String(installationAddressFallback.number)
          : '';
    if (!zipcode || !number) return null;

    const homePassTrimmed = (homePass ?? '').trim();
    if (!homePassTrimmed) return null;

    const normalizedProducts = products.map(p => ({
      id: p.id,
      type: toScheduleProductType(p.type),
      name: p.name,
      price: p.price,
    }));

    return {
      homePass: homePassTrimmed,
      regionId: regionIdNum,
      installationAddress: { zipcode, number },
      products: normalizedProducts,
    };
  }

  getAvailableSlots(
    payload?: SchedulePmeCnpjInput
  ): Observable<ScheduleSlotsResponse> {
    if (!payload) {
      return of({ insufficientSlots: true, slots: [] });
    }
    const normalizedPayload: SchedulePmeCnpjRequest = {
      ...payload,
      ...SCHEDULE_FIXED_CHANNELS,
    };
    return this.apiService
      .postExternal<SchedulePmeCnpjResponse>(this.baseUrl, normalizedPayload)
      .pipe(
        map((res) => this.mapResponseToSlots(res)),
        catchError((err) => {
          console.error('[ScheduleService] Erro ao buscar horários:', err);
          return of({
            insufficientSlots: true,
            slots: [],
          });
        })
      );
  }

  private mapResponseToSlots(
    res: SchedulePmeCnpjResponse
  ): ScheduleSlotsResponse {
    const raw = res.dates ?? [];
    const withProposal = raw.filter(d => !!d.proposal);
    const dates = withProposal.length > 0 ? withProposal : raw;

    const slots: ScheduleSlot[] = dates.map((d) => ({
      startDate: d.proposal?.startDate ?? d.startDate,
      endDate: d.proposal?.endDate ?? d.endDate,
      periodTypeId: d.periodTypeId,
      description: d.description,
      cold: d.emergency ?? false,
      proposal: d.proposal,
    }));
    return {
      insufficientSlots: slots.length < 2,
      slots,
    };
  }
}
